import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EMPTY, Observable, share } from 'rxjs';
import { catchError, finalize, switchMap, tap } from 'rxjs/operators';

import { logError } from '@tk/app/interceptors';
import { AlertifyService } from '@tk/app/toast-manager';
import { AuthService } from '@tk/entities/auth';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private tokenUpdate$: Observable<unknown> | null = null;

  private readonly authService = inject(AuthService);
  private readonly alertify = inject(AlertifyService);

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    request = request.clone({
      setHeaders: {
        Authorization: `Bearer ${this.authService.getToken()}`,
      },
    });

    return next.handle(request).pipe(catchError((error: HttpErrorResponse) => this.handleError(request, error, next)));
  }

  private handleError(
    request: HttpRequest<any>,
    error: HttpErrorResponse,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> | Observable<never> {
    let errorBody = error.error;

    if (request.responseType === 'text') {
      try {
        errorBody = JSON.parse(errorBody);
      } catch {
        /* empty */
      }
    }

    switch (error.status) {
      /**
       * todo: reconsider 403 errors handling
       * https://app.asana.com/1/1202794379193461/project/1205183024062948/task/1210000798377110?focus=true
       */
      case 403:
        if (error.headers.get('token-expired') === 'true') {
          this.logout();
        }
        this.handleExpiredToken()
          .pipe(
            tap(() => this.alertify.informative(errorBody.detail)),
            catchError(() => EMPTY),
          )
          .subscribe((authData) => {
            if (!authData) {
              this.logout();
            }
            logError(request, error, 'error');
            this.alertify.informative('The page will be reloaded in 2 seconds');

            setTimeout(() => {
              window.location.reload();
            }, 2000);
          });

        return EMPTY;

      case 401:
        if (error.headers.get('token-expired') === 'true') {
          return this.handleExpiredToken().pipe(switchMap(() => this.intercept(request, next)));
        }
        throw error;

      default:
        throw error;
    }
  }

  private handleExpiredToken(): Observable<unknown> {
    if (!this.tokenUpdate$) {
      this.tokenUpdate$ = this.authService.refreshToken().pipe(
        catchError((err) => {
          this.logout();
          throw err;
        }),
        finalize(() => (this.tokenUpdate$ = null)),
        share(),
      );
    }

    return this.tokenUpdate$;
  }

  private logout(): void {
    this.authService.logout({ saveLocationForRedirect: true });
  }
}
